/* global alert:false */
import React, { Component } from 'react';
import { Button, Table, Row, Col, Collapse, Card, CardBody } from 'reactstrap';
import { zarString, ageString, upperCaseFirstLetter } from '../../helpers/format';
import { banks, allowedRelationships } from '../../helpers/data';
import api from '../../api';
import SignaturePad from 'signature_pad/dist/signature_pad.js';
let signaturePad;

class CheckoutSummary extends Component {
  constructor (props) {
    super(props);
    this.state = {
      policyholder: {},
      loadingPercentage: 0,
      showFullDeclaration: false
    };
  }

  componentDidMount () {
    const canvas = document.querySelector('canvas');
    signaturePad = new SignaturePad(canvas);
    signaturePad.clear();
  }

  clearSignature (e) {
    e.preventDefault();
    signaturePad.clear();
  }

  updatePolicyholder (policyholder) {
    this.props.setData({ ...this.props.data, policyholder: { ...this.props.data.policyholder, ...policyholder } });
  }

  updateId (id) {
    this.props.setData({ ...this.props.data, policyholder: { ...this.props.data.policyholder, id: { ...this.props.data.policyholder.id, ...id } } });
  }

  getCoverAmount (member) {
    const data = this.props.data;
    switch (member.type) {
      case 'policyholder':
        return member.coverAmount;
      case 'child':
        return data.childrenCoverAmount;
      case 'spouse':
        return data.spouseCoverAmount;
      default:
        return data.extendedFamilyCoverAmount;
    }
  }

  getRelationString (member) {
    switch (member.type) {
      case 'policyholder':
        return 'You';
      case 'child':
        return 'Your child';
      case 'spouse':
        return 'Your spouse';
      default:
        const memberType = allowedRelationships.find(m => m.key === member.type);
        return memberType ? `Your ${memberType.val.toLowerCase()}` : '-';
    }
  }

  getBillingDayText (billingDay) {
    switch (billingDay) {
      case 1:
        return '1st';
      case 7:
        return '7th';
      case 15:
        return '15th';
      case 27:
        return '27th';
    }
  }

  async issuePolicy () {
    if (signaturePad.isEmpty()) {
      return alert('Please sign before we can issue the policy!');
    }

    this.setState({ loading: true, loadingPercentage: 5 });

    // TODO: date format on api. validate age match etc.
    try {
      const data = this.props.data;
      let request = {
        first_name: data.policyholder.firstName,
        last_name: data.policyholder.lastName,
        email: data.policyholder.email,
        cellphone: data.policyholder.cellphone,
        id: { ...data.policyholder.id }
      };

      if (data.policyholder.id.type === 'passport') {
        request = {
          ...request,
          date_of_birth: data.policyholder.dateOfBirth.replace(/-/g, ''),
          gender: data.policyholder.gender
        };
      }

      // Create policyholder
      const policyholder = await api.createPolicyholder(request)
        .catch(async err => {
          if (err.error && err.error.type === 'conflict_error') {
            // Fetch existing policyholder
            const policyholders = await api.getPolicyholders(data.policyholder.id.number)
              .catch(e => Promise.reject(new Error('Could not create policyholder!')));
            if (policyholders && policyholders.length > 0) {
              return policyholders[0];
            } else {
              return Promise.reject(new Error('Could not create policyholder!'));
            }
          } else {
            console.error('Creating policyholder failed:', err);
            return Promise.reject(new Error('Could not create policyholder!'));
          }
        });
      this.setState({ loadingPercentage: 20 });

      // Create application
      const applicationData = {
        quote_package_id: data.quote.quote_package_id,
        policyholder_id: policyholder.policyholder_id,
        spouse: data.spouse
          ? {
            first_name: data.spouse.firstName,
            last_name: data.spouse.lastName,
            date_of_birth: data.spouse.dateOfBirth.replace(/-/g, '')
          }
          : null,
        children: data.children.length > 0
          ? data.children.map(c => ({
            first_name: c.firstName,
            last_name: c.lastName,
            date_of_birth: c.dateOfBirth.replace(/-/g, '')
          }))
          : null,
        extended_family: data.extendedFamily.length > 0
          ? data.extendedFamily.map(m => ({
            first_name: m.firstName,
            last_name: m.lastName,
            date_of_birth: m.dateOfBirth.replace(/-/g, ''),
            relationship: m.type
          }))
          : null
      };
      const application = await api.createApplication(applicationData).catch(err => {
        console.error('Creating application failed:', err);
        return Promise.reject(new Error('Could not create application!'));
      });
      this.setState({ loadingPercentage: 40 });

      let policyRequest = {
        application_id: application.application_id
      };

      if (data.agent.agentCode) {
        policyRequest = {
          ...policyRequest,
          app_data: {
            agent_code: data.agent.agentCode
          }
        };
      }

      // Create policy
      const policy = await api.createPolicy(policyRequest).catch(err => {
        console.error('Creating policy failed:', err);
        return Promise.reject(new Error('Could not create policy!'));
      });

      // Set payment date
      const billingDay = parseInt(data.paymentMethod.billingDay);

      if (billingDay !== 1) {
        await api.updatePolicy(policy.policy_id, {
          billing_day: billingDay
        }).catch(err => {
          console.error('Setting billing day failed:', err);
          return Promise.reject(new Error('Could not set billing day!'));
        });
      }

      this.setState({ loadingPercentage: 60 });

      // Add beneficiaries (equal percentages)
      if (data.includeBeneficiaries) {
        const beneficiaries = data.beneficiaries.map((b, i) => ({
          first_name: b.firstName,
          last_name: b.lastName,
          email: b.email || null,
          cellphone: b.cellphone || null,
          percentage: parseInt(b.percentage),
          id: {
            type: b.id.type,
            country: b.id.country,
            number: b.id.number
          },
          ...(b.includePaymentDetails ? {
            payment_details: {
              type: 'eft',
              details: {
                bank_name: b.bankDetails.bankName,
                branch_code: b.bankDetails.branchCode,
                account_type: b.bankDetails.accountType,
                account_number: b.bankDetails.accountNumber
              }
            }
          } : {})
        }));

        await api.updateBeneficiaries(policy.policy_id, beneficiaries)
          .catch(err => {
            console.error('Adding beneficiaries:', err);
            return Promise.reject(new Error('Could not add payment beneficiaries!'));
          });
      }

      // TODO: make generic
      const bankDetails = data.paymentMethod.bankDetails;
      // Add payment method
      await api.addPaymentMethod(policyholder.policyholder_id, {
        type: 'debit_order',
        bank_details: {
          account_holder: bankDetails.accountHolder,
          account_number: bankDetails.accountNumber,
          branch_code: bankDetails.branchCode,
          bank: bankDetails.bankName
        },
        policy_ids: [policy.policy_id]
      }).catch(err => {
        console.error('Adding payment method failed:', err);
        return Promise.reject(new Error('Could not add payment method!'));
      });
      this.setState({ loadingPercentage: 80 });

      // Upload signature
      await api.createAttachment(policy.policy_id, {
        file_name: 'Authorisation Signature.png',
        description: 'Policy and debit order authorisation signature.',
        file_base64: signaturePad.toDataURL('image/png').slice('data:image/png;base64,'.length),
        file_type: 'image/png'
      }).catch(err => {
        console.error('Adding signature failed:', err);
        return Promise.reject(new Error('Could not add signature!'));
      });
      this.setState({ loadingPercentage: 100 });

      // Show success page
      this.props.setData({ ...data, policyNumber: policy.policy_number });
      this.props.nextStep();
    } catch (error) {
      this.setState({ loading: false });
      return alert('Something went wrong. ' + error.message);
    }
  }

  renderPremium (premium) {
    const amount = zarString(premium).replace('R', '');
    const data = this.props.data;
    const billingDay = parseInt(data.paymentMethod.billingDay);

    return <div>
      <h5>Monthly premium</h5>
      <div className='premium'>
        <span className='currency'><b>R</b></span>
        <span className='amount'><b>{amount}</b></span>
      </div>
      <br />
      <p>
        <b>Payment date: </b>{this.getBillingDayText(billingDay)} of each month
      </p>
    </div>;
  }

  renderRow (member, index) {
    const name = member.type === 'policyholder' ? 'Policyholder' : `${member.firstName} ${member.lastName}`;
    const cover = zarString(this.getCoverAmount(member));
    const age = ageString(member.dateOfBirth);
    const gender = (member.gender && upperCaseFirstLetter(member.gender)) || '-';
    const relation = this.getRelationString(member);
    return <tr key={`${member.type}-${index || ''}`}>
      <td><b>{name}</b></td>
      <td>{cover}</td>
      <td>{age}</td>
      <td>{gender}</td>
      <td>{relation}</td>
    </tr>;
  }

  renderTable () {
    const data = this.props.data;
    return <Table bordered>
      <thead>
        <tr className='table-active'>
          <th colSpan='5'>Included in quote</th>
        </tr>
      </thead>
      <tbody>
        {this.renderRow(data.policyholder)}
        {data.spouse ? this.renderRow(data.spouse) : undefined}
        {data.children.map((child, i) => this.renderRow(child, i))}
        {data.extendedFamily.map((member, i) => this.renderRow(member, i))}
      </tbody>
    </Table>;
  }

  renderPaymentDetails () {
    const paymentMethod = this.props.data.paymentMethod;
    const bank = banks.find(b => paymentMethod.bankDetails && b.key === paymentMethod.bankDetails.bankName);
    const bankDetails = paymentMethod.bankDetails;

    return <div>
      <h5>Payment details</h5>
      <br />
      {
        paymentMethod.type === 'debit_order'
          ? <Table borderless style={{ lineHeight: 0 }}>
            <tbody>
              <tr>
                <td style={{ paddingLeft: 0 }}><b>Payment method</b></td>
                <td>Debit order</td>
              </tr>
              <tr>
                <td style={{ paddingLeft: 0 }}><b>Bank</b></td>
                <td>{bank && bank.val}</td>
              </tr>
              <tr>
                <td style={{ paddingLeft: 0 }}><b>Account number</b></td>
                <td>{bankDetails && bankDetails.accountNumber}</td>
              </tr>
              <tr>
                <td style={{ paddingLeft: 0 }}><b>Account holder</b></td>
                <td>{bankDetails && bankDetails.accountHolder}</td>
              </tr>
              <tr>
                <td style={{ paddingLeft: 0 }}><b>Branch code</b></td>
                <td>{bankDetails && bankDetails.branchCode}</td>
              </tr>
            </tbody>
          </Table>
          : <p>TODO</p>
      }
    </div>;
  }

  renderDeclaration () {
    const policyholder = this.props.data.policyholder;
    return <div>
      <h5>Declaration</h5>
      <br />
      <p>
        I {policyholder.firstName} {policyholder.lastName}, {policyholder.id.type === 'id' ? 'ID' : 'passport'} number {policyholder.id.number}, confirm that:
      </p>
      <ul>
        <li>The information in this application is true and correct; and</li>
        <li>I have read and agree to abide by the terms set out in the <a target='_blank' href='/funeral/terms.pdf'>policy document</a>; and
        </li>
        <li>I am aware of no other information which might be relevant to Guardrisk Life Limited's decision to offer me
          cover.
        </li>
        <li>I hereby authorise the issuing of a payment instruction to collect the monthly premium by debit order from my bank account. <a href='#' onClick={e => { e.preventDefault(); this.setState({ showFullDeclaration: !this.state.showFullDeclaration }); }}>View full declaration</a>.</li>
      </ul>
    </div>;
  }

  renderGeneralExclusions () {
    return <div>
      <h5>General Exclusions</h5>
      <br />
      <p>
      A summary of the general exclusions as set out in the terms and conditions are listed below.
      The insurer will not be obliged to pay any claim which is, directly or indirectly, caused by or connected to the following:
      </p>
      <ul>
        <li>Any pre-existing condition of which you or your family members covered
          on this policy are aware of for the first 6 months of the policy;</li>
        <li>An event where the insured is over the legal alcohol limit permitted by law;</li>
        <li>An event where the insured commits any breach of (any) law or criminal law;</li>
        <li>Illegal acts of the insured or the insured’s participation in any criminal activities;</li>
        <li>Intentionally self-inflicted injury, suicide or a suicide attempt within the first 12 months of the policy.</li>
      </ul>
      <p>No payment will be made under this policy if the
        premiums have not been paid up to date or if the insured has not
        complied with all the obligations and conditions of the Policy.</p>
    </div>;
  }

  renderFullDeclaration () {
    return <div>
      <Collapse isOpen={this.state.showFullDeclaration}>
        <Card>
          <CardBody>
            <b>Full declaration</b>
            <p>
I hereby authorise the issuing of a payment instruction to collect the monthly premium by debit order from my bank account on condition that the sum of such payment instruction will never exceed my obligation in terms of this application.
            </p>

            <p>
Guardrisk Life Limited has appointed XI Platform (Pty) Ltd to collect premiums on its behalf. I acknowledge that all payment instructions issued by XI Platform, on behalf of Guardrisk shall be treated by my bank as if the instruction has been issued by me personally. The debit order will be collected every month on the debit order collection date selected (as confirmed above). In the event that this collection day falls on a Sunday or a recognised South African public holiday, the collection day will automatically be the next ordinary business day.
            </p>

            <p>
I acknowledge that this authority may be assigned to a third party only if the policy is transferred to another insurer or administrator. I understand that the payment instruction will be processed through a computerised system provided by the South African Banks.
            </p>

            <p>
I shall not be entitled to any refund of amounts which XI Platform as the payment distribution agent on behalf of Guardrisk has collected while this debit order authority was in force if such amounts were legally owing to Guardrisk.
            </p>

            <p>
This authority may be cancelled by giving Oats Risk Services notice of not less than 30 days and such cancellations will not necessarily cancel my policy.
            </p>
          </CardBody>
        </Card>
      </Collapse>
    </div>;
  }

  render () {
    return <div>
      <h1>Policy confirmation</h1>
      <br />
      <h5>Please check all your policy details before checking out.</h5>
      <br />
      <hr />
      <br />
      {/* Payment section */}
      <Row>
        <Col sm={6}>
          {this.renderPremium(this.props.data.quote.suggested_premium)}
        </Col>
        <Col sm={6}>
          {this.renderPaymentDetails()}
        </Col>
      </Row>
      <br />
      {this.renderTable()}
      <br />
      {this.renderDeclaration()}
      <br />
      {this.renderGeneralExclusions()}
      <br />
      {this.renderFullDeclaration()}
      <br />
      <h5 style={{marginBottom: '12px'}}>Sign to accept policy</h5>
      <canvas className='signature-canvas' />
      <Button color='link' onClick={this.clearSignature} style={{paddingLeft: 0}}>Clear</Button>

      <br />
      <br />
      <Button color='primary' className='px-5' onClick={this.issuePolicy.bind(this)}disabled={this.state.loading}>{this.state.loading ? 'Loading...' : 'Take out policy'}</Button>
      <Button color='link' onClick={this.props.previousStep}>Back</Button>

      {/* Loader */}
      <FullScreenLoader active={this.state.loading} percentage={this.state.loadingPercentage} />
    </div>;
  }
}

class FullScreenLoader extends Component {
  render () {
    return <div>
      <section className={'full-screen-loader' + (this.props.active ? ' active' : '')}>
        <div>
          <h3>Busy issuing your new policy...</h3>
          <div className='line'>
            <div className='loader' style={{ width: `${this.props.percentage || 0}%` }} />
          </div>
        </div>
      </section>
    </div>;
  }
}

export default CheckoutSummary;



// WEBPACK FOOTER //
// ./src/components/application/checkout-summary.js